import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { BaseOperation } from '../../models/thermal-operation.model';
import { TemperatureResultPhase, TemperatureResults, TemperatureResultsUi } from '../../models/thermal-results.model';
import { ThermalOperationsService } from '../../services/thermal-operations.service';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { ChangeSelectedThermalOperation, ChangeSelectedThermalOperationType, SyncPressAndTempResults } from 'src/app/shared/models/mediator-messages.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { Store } from '@ngneat/elf';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { cloneDeep, zipObject } from 'lodash-es';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { CurrentAppIds } from 'src/app/core/components/user-admin-page/user-model';
import { formatUnitNumber } from 'src/app/shared/helpers/unit-number-format.helper';

@Component({
  selector: 'app-thermal-temperature-results',
  templateUrl: './thermal-temperature-results.component.html',
  styles: [``],
  standalone: false
})
export class ThermalTemperatureResultsComponent implements OnInit, OnDestroy {

  public isLoading = signal<boolean>(true);
  public missingOperations = false;
  public needToCalculate: boolean;
  public plotName: string;

  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' },
    { label: 'Heatmap', value: 'heatmap' }
  ];

  public singleMultiple: SelectItem[] = [
    { label: 'Single', value: 'singleOperation' },
    { label: 'Multiple', value: 'multipleOperation' },
    { label: 'Operation Type', value: 'operationType' }
  ];

  public operationTypes: SelectItem[] = [
      { label: 'Production', value: 'production' },
      { label: 'Injection', value: 'injection' },
      { label: 'Circulation', value: 'circulation' },
      { label: 'Trip Pipe & Circulate', value: 'tripPipeAndCirculate' },
      { label: 'Run Casing & Circulate', value: 'runCasingAndCirculate' },
      { label: 'Drilling', value: 'drilling' },
      { label: 'Cementing', value: 'cementing' },
      { label: 'Shut In', value: 'shutIn' },
      { label: 'Screenout', value: 'screenout' }
  ];

  public selectedOperationType = 'drilling';

  public depthView = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];

  public operations: Partial<BaseOperation>[];
  public allOperationsResults: TemperatureResults[];
  public results: any[];

  get operationsWithNone(): Partial<BaseOperation>[] {
    if (!this.operations) return [];
    return this.operations;
  }
  public resultsBackup: any[];
  public resultsGrid: any[];
  public cols: any[];
  public phases: TemperatureResultPhase[];
  public calculationError = "Please calculate PERICAL to see results.";
  public multipleResultsFull: any[] = [];
  public annulusNames: { label: string, value: string }[] = [];
  public operationNames: any[] = [];
  public operationNamesPlot: { field: string, header: string }[] = [];
  public selectedOperationIndex = 0;
  public moveUpDisabled = true;
  public moveDownDisabled = false;
  public temperatureUnit: string;
  public depthUnit: string;
  public diameterUnit: string;
  public shortLengthUnit = 'in';
  public readonly formatAnnulusOption = (option: { label: string }): string => {
    const unit = this.shortLengthUnit;
    return option.label.replace(/\d+\.\d+/g, value =>
      formatUnitNumber(Number(value), unit, undefined, undefined, { [unit]: unit === 'in' ? 3 : 2 })
    );
  };
  public minTemp: number;
  public maxTemp: number;
  public tubulars: any[];
  public annulusName: string;
  public selectedStringId: string;

  private _subscriptions: Subscription = new Subscription();

  // State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.temperatureResultsStore = this._storeService.createStore(this.componentId, new TemperatureResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedSingleMultiple = this.temperatureResultsStore.state.selectedSingleMultiple || 'singleOperation';
    this.selectedResultsDisplay = this.temperatureResultsStore.state.selectedResultsDisplay || 'plot';
    this.showHeatmap = this.temperatureResultsStore.state.showHeatmap ?? true;
    this.selectedDepthView = this.temperatureResultsStore.state.selectedDepthView || 'md';
    this.syncThermalResults = this.temperatureResultsStore.state.syncThermalResults ?? true;
    this.selectedOperation = this.temperatureResultsStore.state.selectedOperation;
    this.selectedAnnulus = this.temperatureResultsStore.state.selectedAnnulus;
    this.selectedPhase = this.temperatureResultsStore.state.selectedPhase;
    this.showWellborePoints = this.temperatureResultsStore.state.showWellborePoints ?? true;
    this.showFormationPoints = this.temperatureResultsStore.state.showFormationPoints ?? false;
  }
  get componentId(): string {
    return this._componentId;
  }
  public temperatureResultsStore: Store;

  // Local properties for store state (Stage 1 migration)
  public selectedSingleMultiple: string = 'singleOperation';
  public selectedResultsDisplay: string = 'plot';
  public showHeatmap: boolean = true;
  public selectedDepthView: string = 'md';
  public syncThermalResults: boolean = true;
  public selectedOperation: Partial<BaseOperation>;
  public selectedAnnulus: any;
  public selectedPhase: TemperatureResultPhase;
  public showWellborePoints: boolean = true;
  public showFormationPoints: boolean = false;

  constructor(
    private _thermalOperationsService: ThermalOperationsService,
    private _messenger: MediatorService,
    private _signalRService: SignalRService,
    private _wellConfigService: WellConfigService,
    private _storeService: StoreService
  ) {
    this._subscriptions.add(this._messenger.of(ChangeSelectedThermalOperation).subscribe((e) => {
      if (this.selectedSingleMultiple === 'multipleOperation') {
        return;
      }
      let selectedOperation;
      if (!e.selectedThermalOperation.value?.['phaseNumber']) {
        selectedOperation = e.selectedThermalOperation;
        this.selectedOperation = selectedOperation.value;
        this.temperatureResultsStore.update((state) => ({ ...state, selectedOperation: selectedOperation.value}));

        if (!this.selectedSingleMultiple) {
          this.selectedSingleMultiple = 'singleOperation';
          this.temperatureResultsStore.update(state => ({ ...state, selectedSingleMultiple: 'singleOperation' }));
        }

        this.onOperationSelected(selectedOperation, false);
      } else {
        selectedOperation = this.selectedOperation;
        this.selectedPhase = this.allOperationsResults.find(x => x.operationId === this.selectedOperation.id).phases.find(x => x.phaseNumber === e.selectedThermalOperation.value['phaseNumber']);
        this.temperatureResultsStore.update((state) => ({ ...state, selectedPhase: this.selectedPhase }));

        if (!this.selectedSingleMultiple) {
          this.selectedSingleMultiple = 'singleOperation';
          this.temperatureResultsStore.update(state => ({ ...state, selectedSingleMultiple: 'singleOperation' }));
        }

        this.onOperationSelected(selectedOperation, false, this.selectedPhase);
      }
      this.plotName = this.setPlotName();
    }));

    this._subscriptions.add(this._messenger.of(SyncPressAndTempResults).subscribe((e) => {
      this.syncThermalResults = e.syncPressAndTemp;
      this.temperatureResultsStore.update(state => ({
        ...state,
        syncThermalResults: e.syncPressAndTemp
      }));
    }));

    this._subscriptions.add(this._messenger.of(ChangeSelectedThermalOperationType).subscribe(({ operationType }) => {
      if (this.selectedSingleMultiple !== 'operationType' || this.selectedOperationType === operationType) {
        return;
      }

      this.selectedOperationType = operationType;
      this.remapMultipleResults(0);
      this.plotName = this.setPlotName();
      this.annulusName = this.selectedAnnulus?.label;
    }));
  }

  async ngOnInit(): Promise<void> {
    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.temperatureUnit = uu.temperature;
    this.depthUnit = uu.longLengths;
    this.diameterUnit = uu.shortLengths == 'in' ? '"' : uu.shortLengths;
    this.shortLengthUnit = uu.shortLengths;

    this.selectedStringId = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS).tubularId;
    await this.getTemperatureResults(this.selectedSingleMultiple === 'operationType' ? true : false);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  private signalRfunc(data: any): void {
    if (data.action === PeriforOnChangeMessages.REFRESH_THERMAL_RESULTS || data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.isLoading.set(true);
      const appIds = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS);
      this.selectedStringId = appIds.tubularId;
      this.getTemperatureResults(this.selectedSingleMultiple === 'operationType' ? true : false);
      if (this.selectedSingleMultiple === 'multipleOperation') {
        this.selectedAnnulus = this.annulusNames[0];
        this.temperatureResultsStore.update(state => ({ ...state, selectedAnnulus: this.annulusNames[0] }));
      }
    }
  }

  get isDialogVisible(): boolean {
    return !this.missingOperations && this.needToCalculate;
  }

  private async getTemperatureResults(getWholeDesignResults = false): Promise<void> {
    const sources = [
      this._wellConfigService.getTubulars(),
      this._thermalOperationsService.getThermalTemperatureResults(false, getWholeDesignResults) as Observable<any>,
      this._thermalOperationsService.getThermalOperations()
    ];

    forkJoin(sources).subscribe({
      next: ([tubulars, allResults, operations]) => {
        this.operations = operations;
        if (!this.selectedOperation || !this.operations.find(o => o.id === this.selectedOperation.id)) {
          this.selectedOperation = this.operations[0];
          this.temperatureResultsStore.update(state => ({
            ...state,
            selectedOperation: this.selectedOperation
          }));
        }
        this.missingOperations = this.operations.length === 0;
        if (this.missingOperations) {
          this.results = [];
        }
        if (this.operations.length == 0) {
          this.isLoading.set(false);
          // this.operations = null;
          this.missingOperations = true;
          return;
        }

        this.tubulars = tubulars;

        this.allOperationsResults = allResults;
        this.resultsBackup = allResults;

        if (this.allOperationsResults.length > 0) {

          if (this.allOperationsResults.length < 2) {
            this.moveUpDisabled = true;
            this.moveDownDisabled = true;
          }

          if (this.selectedSingleMultiple === 'singleOperation') {
            const selectedOperation = this.allOperationsResults.find(x => x.operationId === this.selectedOperation.id);
            this.selectedPhase = selectedOperation?.phases[selectedOperation.phases.length - 1];
            this.temperatureResultsStore.update(state => ({ ...state, selectedPhase: this.selectedPhase }));
            this.plotName = this.setPlotName();
            this.remapResults();
          } else {
            this.remapMultipleResults(0);
            this.plotName = this.setPlotName();
          }
        } else {
          this.clearResults();
          this.calculationError = "Please calculate PERICAL to see results.";
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.missingOperations = true;
        this.isLoading.set(false);
      }
    });
  }

  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.temperatureResultsStore.update(state => ({
      ...state,
      selectedDepthView: e.value
    }));
  }

  setPlotName(): string {
    if (this.selectedSingleMultiple === 'singleOperation') {
      return 'thermalTemperatureResults' + this.selectedResultsDisplay + ' - ' +
        this.selectedOperation?.id + ' ' + this.selectedPhase?.phaseNumber;
    } else {
      return 'thermalTemperatureResults' + ' - ' + this.selectedStringId + ' - ' + this.selectedResultsDisplay + ' - ' +
        this.selectedAnnulus?.label;
    }
  }

  public onSelectedResultsDisplayUpdated(e) {
    this.selectedResultsDisplay = e.value;
    this.temperatureResultsStore.update(state => ({
      ...state,
      selectedResultsDisplay: e.value
    }));
    this.plotName = this.setPlotName();
  }

  public syncPressAndTemp(e) {
    this.syncThermalResults = e.checked;
    this.temperatureResultsStore.update(state => ({
      ...state,
      syncThermalResults: e.checked
    }));
    this._messenger.publish(new SyncPressAndTempResults(this.syncThermalResults));
    this._messenger.publish(new ChangeSelectedThermalOperation(this.selectedOperation));
  }

  public onOperationSelected(e, isCalledFromHere, selectedPhase?: TemperatureResultPhase): void {
    if (isCalledFromHere && this.syncThermalResults) {
      this._messenger.publish(new ChangeSelectedThermalOperation(e));
    }
    this.moveUpDisabled = false;
    this.moveDownDisabled = false;
    if (this.allOperationsResults[0].operationId === (e.value?.id ?? e.id)) {
      this.moveUpDisabled = true;
    }
    if (this.allOperationsResults[this.allOperationsResults.length - 1].operationId === (e.value?.id ?? e.id)) {
      this.moveDownDisabled = true;
    }
    if (this.allOperationsResults.length > 0) {
      const firstOperation = this.allOperationsResults.find(x => x.operationId === (e.value?.id ?? e.id));
      this.selectedOperation = e.value ?? e;
      this.selectedPhase = !selectedPhase ? firstOperation?.phases[firstOperation.phases.length - 1] : selectedPhase;
      this.temperatureResultsStore.update(state => ({
        ...state,
        selectedOperation: this.selectedOperation,
        selectedPhase: this.selectedPhase
      }));
      this.plotName = this.setPlotName();
      this.remapResults(selectedPhase);
    } else {
      this.needToCalculate = true;
    }
  }

  public onUpDownButtonClick(e, isCalledFromHere): void {
    this.moveUpDisabled = false;
    this.moveDownDisabled = false;
    if (this.selectedSingleMultiple === 'singleOperation') {
      this.moveSingleOperations(e, isCalledFromHere);
    } else {
      this.moveMultipleOperations(e);
    }
  }

  private moveMultipleOperations(e: 'up' | 'down') {
    this.annulusNames = this.selectedPhase.temperatureResultsTable.headers.slice(2).map(h => {
      const cleanLabel = h
        .replace(/"/g, '\u2033'); // Replace double quotes with ″ (prime symbol)

      return {
        value: toCamelCaseString(h),
        label: cleanLabel
      };
    });

    const offset = e === 'up' ? -1 : 1;
    this.moveSelectedAnnulus(offset);

    this.moveUpDisabled = this.annulusNames.findIndex(a => a.value === this.selectedAnnulus?.value) === 0;
    this.moveDownDisabled = this.annulusNames.findIndex(a => a.value === this.selectedAnnulus?.value) === this.annulusNames.length - 1;
  }

  private moveSelectedAnnulus(offset: number): void {
    const currentValue = this.selectedAnnulus?.value;

    if (!currentValue || !this.annulusNames?.length) return;

    const currentIndex = this.annulusNames.findIndex(x => x.value === currentValue);
    const targetIndex = currentIndex + offset;

    if (targetIndex < 0 || targetIndex >= this.annulusNames.length) return;

    const matched = this.annulusNames[targetIndex];

    this.selectedAnnulus = matched;
    this.temperatureResultsStore.update(state => ({
      ...state,
      selectedAnnulus: matched
    }));

    this.plotName = this.setPlotName();
    this.remapMultipleResults(0);
  }

  private moveSingleOperations(e, isCalledFromHere) {
    this.selectedOperationIndex = this.operations.findIndex(x => x.id === this.selectedOperation.id);
    if (e === 'up') {
      const selectedOperation = this.operations[this.selectedOperationIndex - 1];
      if (!selectedOperation) {
        this.moveUpDisabled = true;
        return;
      }
      if (isCalledFromHere && this.syncThermalResults) {
        this._messenger.publish(new ChangeSelectedThermalOperation(selectedOperation));
      }
      const firstOperation = this.allOperationsResults[this.selectedOperationIndex];
      if (!firstOperation) {
        this.clearResults();
      }
      this.selectedOperation = selectedOperation;
      this.selectedPhase = firstOperation?.phases[firstOperation.phases.length - 1];
      this.temperatureResultsStore.update(state => ({
        ...state,
        selectedOperation: selectedOperation,
        selectedPhase: this.selectedPhase
      }));
      this.selectedOperationIndex = this.operations.findIndex(x => x.id === this.selectedOperation.id);
      this.moveUpDisabled = this.selectedOperationIndex === 0 ? true : false;
      this.moveDownDisabled = this.selectedOperationIndex === this.operations.length - 1 ? true : false;
      this.remapResults();
    } else {
      const selectedOperation = this.operations[this.selectedOperationIndex + 1];
      if (!selectedOperation) {
        this.moveDownDisabled = true;
        return;
      }
      if (isCalledFromHere && this.syncThermalResults) {
        this._messenger.publish(new ChangeSelectedThermalOperation(selectedOperation));
      }
      const firstOperation = this.allOperationsResults[this.selectedOperationIndex];
      if (!firstOperation) {
        this.clearResults();
      }
      this.selectedOperation = selectedOperation;
      this.selectedPhase = firstOperation?.phases[firstOperation.phases.length - 1];
      this.temperatureResultsStore.update(state => ({
        ...state,
        selectedOperation: selectedOperation,
        selectedPhase: this.selectedPhase
      }));
      this.selectedOperationIndex = this.operations.findIndex(x => x.id === this.selectedOperation.id);
      this.moveUpDisabled = this.selectedOperationIndex === 0 ? true : false;
      this.moveDownDisabled = this.selectedOperationIndex === this.operations.length - 1 ? true : false;
      this.remapResults();
    }
    this.plotName = this.setPlotName();
  }

  private clearResults() {
    this.moveDownDisabled = true;
    this.moveUpDisabled = true;
    this.results = [];
    this.cols = [];
    this.resultsGrid = [];
    this.multipleResultsFull = [];
    this.needToCalculate = true;
    this.plotName = null;
    return
  }

  public onPhaseSelected(results: any[], isCalledFromHere: boolean): void {
    if (isCalledFromHere && this.syncThermalResults) {
      this._messenger.publish(new ChangeSelectedThermalOperation(results));
    }
    if (this.allOperationsResults.length > 0) {
      this.selectedPhase = results['value'];
      this.temperatureResultsStore.update(state => ({ ...state, selectedPhase: results['value'] }));
      this.plotName = this.setPlotName();
      this.remapResults(results['value']);
    } else {
      this.needToCalculate = true;
    }
  }

  public toggleFormationPoints(e): void {
    this.showFormationPoints = e.checked;
    this.temperatureResultsStore.update(state => ({ ...state, showFormationPoints: e.checked }));
    if (!this.showWellborePoints) {
      this.showWellborePoints = true;
      this.temperatureResultsStore.update(state => ({ ...state, showWellborePoints: true }));
    }
    this.allOperationsResults = cloneDeep(this.resultsBackup);
    this.remapResults();
  }

  public toggleWellborePoints(e): void {
    this.showWellborePoints = e.checked;
    this.temperatureResultsStore.update(state => ({ ...state, showWellborePoints: e.checked }));
    if (!this.showFormationPoints) {
      this.showFormationPoints = true;
      this.temperatureResultsStore.update(state => ({ ...state, showFormationPoints: true }));
    }
    this.allOperationsResults = cloneDeep(this.resultsBackup);
    this.remapResults();
  }

  public modeToggle(e) {
    this.moveUpDisabled = true;
    this.moveDownDisabled = false;
    this.selectedSingleMultiple = e;
    this.temperatureResultsStore.update(state => ({
      ...state,
      selectedSingleMultiple: e
    }));
    if (e === 'singleOperation') {
      this.syncThermalResults = true;
      this.temperatureResultsStore.update(state => ({ ...state, syncThermalResults: true }));
      this._messenger.publish(new SyncPressAndTempResults(this.syncThermalResults));
      this.selectedOperation = this.operations[0];
      this.temperatureResultsStore.update(state => ({
        ...state,
        selectedOperation: this.selectedOperation
      }));
      this.remapResults(null);
      this.annulusName = null;
    } else if (e === 'multipleOperation') {
      this.syncThermalResults = false;
      this.temperatureResultsStore.update(state => ({ ...state, syncThermalResults: false }));
      this.remapMultipleResults(0);
      // this.annulusName = this.annulusNames.find(x => x.value == this.selectedAnnulus.value)?.label;
    } else {
      this.syncThermalResults = false;
      this.temperatureResultsStore.update(state => ({ ...state, syncThermalResults: false }));

      this.getTemperatureResults(true);
      this.getTemperatureResults(true);

      this.remapMultipleResults(0);
    }
    this.plotName = this.setPlotName();
  }

  public annulusDropdownChange(e) {
    this.moveUpDisabled = false;
    this.moveDownDisabled = false;
    this.selectedAnnulus = e.value;
    this.temperatureResultsStore.update(state => ({
      ...state,
      selectedAnnulus: e.value
    }));
    this.plotName = this.setPlotName();
    this.remapMultipleResults(0);
    this.annulusName = this.annulusNames.find(x => x.value == this.selectedAnnulus?.value)?.label;
  }

  public onOperationTypeSelected(operationType: string): void {
    this.selectedOperationType = operationType;
    this.remapMultipleResults(0);
    this.plotName = this.setPlotName();
    this.annulusName = this.selectedAnnulus?.label;
    this._messenger.publish(new ChangeSelectedThermalOperationType(operationType));
  }

  public remapMultipleResults(result: any, selectedPhase?: TemperatureResultPhase): void {
    const allSelectedTypeOperations: any[] = [];

    if (this.selectedSingleMultiple === 'operationType') {

      this.allOperationsResults.forEach(x => {
        if (x.operationType == this.selectedOperationType) {
          allSelectedTypeOperations.push(x);
        }
      });
    }

    let allOperationsResults = this.selectedSingleMultiple === 'operationType' ?
      allSelectedTypeOperations.filter(ar => ar.phases[0]) : this.allOperationsResults.filter(ar => this.operations.find(rm => (rm.name === ar.name)));

    this.selectedOperation = allOperationsResults[0];
    this.temperatureResultsStore.update(state => ({
      ...state,
      selectedOperation: this.selectedOperation
    }));

    const selectedOperationResults = allOperationsResults[0];
    this.needToCalculate = !selectedOperationResults;
    this.phases = selectedOperationResults ? selectedOperationResults.phases : null;
    if (!selectedPhase) {
      selectedPhase = this.phases ? this.phases[this.phases.length - 1] : null;
      this.selectedPhase = selectedPhase;
      this.temperatureResultsStore.update(state => ({ ...state, selectedPhase: selectedPhase }));
    }

    if (this.selectedSingleMultiple === 'operationType' && !selectedPhase) {
      this.results = [];
      this.cols = [];
      this.resultsGrid = [];
      this.multipleResultsFull = [];
      this.calculationError = "Please make sure that " + this.getOperationTypeName() + " operations are calculated.";
      this.missingOperations = null;
      return;
    } else if (this.operations.length === 0) {
      this.missingOperations = true;
    }

    this.annulusNames = this.selectedPhase.temperatureResultsTable.headers.slice(2).map(h => {
      const cleanLabel = h
        .replace(/"/g, '\u2033'); // Replace double quotes with ″ (prime symbol)

      return {
        value: toCamelCaseString(h),
        label: cleanLabel
      };
    });

    if (this.selectedSingleMultiple === 'operationType') {
      this.selectedAnnulus = this.annulusNames[0];
      this.temperatureResultsStore.update(state => ({ ...state, selectedAnnulus: this.annulusNames[0] }))

      this.operationNames = allSelectedTypeOperations.map(x => {
        return {
          field: toCamelCaseString(x.name),
          header: x.name,
          valueFormatter: (params: any) => this.formatDecimal(params)
        };
      });
    } else {
      allOperationsResults = allOperationsResults.sort((a, b) => {
        return this.operations.findIndex(x => a.name == x.name) - this.operations.findIndex(x => b.name == x.name);
      });

      this.operationNames = allOperationsResults.map(x => {
        return {
          field: toCamelCaseString(x.name),
          header: x.name,
          valueFormatter: (params: any) => this.formatDecimal(params)
        };
      });
    }

    this.multipleResultsFull = [];
    const allOpResultsLength = allOperationsResults.length;

    if (allOpResultsLength > 0 && this.selectedSingleMultiple === 'operationType') {
      this.missingOperations = null;
    } else if (this.operations.length === 0) {
      this.missingOperations = true;
    }

    let longestOperationDataIndex = 0;
    let dataLength = 0;
    for (let l = 0; l < allOpResultsLength; l++) {
      const operation = allOperationsResults[l];

      if (operation.phases.length === 0) {
        continue;
      }

      const { length: phaseLength } = operation.phases[0].temperatureResultsTable.rows;

      if (dataLength < phaseLength) {
        longestOperationDataIndex = l;
        dataLength = phaseLength;
      }
    }

    const opPhaseIndex = allOperationsResults[longestOperationDataIndex].phases;
    const depths = opPhaseIndex[0].temperatureResultsTable.rows.map(x => x[0]);

    let operationCount = 0;
    const rowLength = opPhaseIndex[0].temperatureResultsTable.rows.length;
    for (let l = 0; l < rowLength; l++) {
      const multipleResults = [];
      // let lastPhase;
      for (let i = 0; i < allOpResultsLength; i++) {
        const lastPhaseNew = allOperationsResults[i].phases.length - 1;
        if (allOperationsResults[i].phases.length == 0) {
          continue;
        }
        // lastPhase = allOperationsResults[operationCount].phases.length - 1;
        if (i === 0) {
          const row = opPhaseIndex[opPhaseIndex.length - 1].temperatureResultsTable.rows[l];
          multipleResults.push(row[0]);
          multipleResults.push(row[1]);
        }
        const lastPhaseTempResultsAtIndex = allOperationsResults[i].phases[lastPhaseNew].temperatureResultsTable;
        let annulusColumnIndex = lastPhaseTempResultsAtIndex.headers.findIndex(x => toCamelCaseString(x) == (this.selectedAnnulus?.value || this.annulusNames[0].value));
        if (annulusColumnIndex < 0) {
          annulusColumnIndex = 2;
        }

        const longestOpPhaseIndex = opPhaseIndex.length - 1;
        const longestOpPhaseIndexRows = opPhaseIndex[longestOpPhaseIndex].temperatureResultsTable.rows;
        if (l < longestOpPhaseIndexRows.length) {
          const currentDepth = longestOpPhaseIndexRows[l][0];
          const depthIndex = depths.findIndex(x => x == currentDepth);

          if (depths[depthIndex] == currentDepth) {
            const rowIndex = allOperationsResults[i].phases[0].temperatureResultsTable.rows.findIndex(x => x[0] == currentDepth);
            // lastPhase = allOperationsResults[i].phases.length - 1;
            const lastPhaseAtIndexRowLength = lastPhaseTempResultsAtIndex.rows.length;
            const currentDepthCheck = lastPhaseTempResultsAtIndex.rows[lastPhaseAtIndexRowLength - 1][0];
            if (l < lastPhaseAtIndexRowLength && rowIndex >= 0 && currentDepth <= currentDepthCheck) {
              multipleResults.push(lastPhaseTempResultsAtIndex.rows[rowIndex][annulusColumnIndex]);
            } else if (currentDepth <= currentDepthCheck) {
              const currentOperationDepths = lastPhaseTempResultsAtIndex.rows.map(x => x[0]);
              const previousDepthIndex = currentOperationDepths.findIndex(x => x >= currentDepth) - 1;

              if (previousDepthIndex < 0) {
                multipleResults.push(null);
                continue;
              }
              const nextDepthIndex = currentOperationDepths.findIndex(x => x >= currentDepth);
              const previousDepth = currentOperationDepths[previousDepthIndex];
              const nextDepth = currentOperationDepths[nextDepthIndex];

              const [previousTemp, nextTemp] = [
                lastPhaseTempResultsAtIndex.rows[previousDepthIndex][annulusColumnIndex],
                lastPhaseTempResultsAtIndex.rows[nextDepthIndex][annulusColumnIndex]
              ];

              const interpolatedTemperature = previousTemp +
                ((nextTemp - previousTemp) / (nextDepth - previousDepth)) * (currentDepth - previousDepth);

              multipleResults.push(interpolatedTemperature);
            } else {
              multipleResults.push(null);
            }
          }
        } else {
          multipleResults.push(null);
        }
      }

      if (l === allOperationsResults[operationCount].phases[allOperationsResults[operationCount].phases.length - 1].temperatureResultsTable.rows.length - 1) {
        operationCount++;
      }

      this.multipleResultsFull.push(multipleResults);
    }

    if (this.selectedSingleMultiple === 'multipleOperation') {
      this.operationNames = this.operationNames.filter(ar => allOperationsResults.find(rm => (ar.header.startsWith(rm.name))));
    }

    this.operationNames.unshift({ field: 'tvdFt', header: `TVD (${this.depthUnit})`, valueFormatter: (params: any) => this.formatDecimal(params) });
    this.operationNames.unshift({ field: 'mdFt', header: `MD (${this.depthUnit})`, valueFormatter: (params: any) => this.formatDecimal(params) });

    this.operationNames.push({
      field: 'udt',
      header: 'UDT',
      valueFormatter: (params: any) => this.formatDecimal(params)
    });

    this.resultsGrid = this.multipleResultsFull.map(r => zipObject(this.operationNames.map(c => c.field), r));

    const longestOpIndexPhases = allOperationsResults[longestOperationDataIndex].phases;
    const longestOpPhaseIndex = longestOpIndexPhases[longestOpIndexPhases.length - 1];
    const resGridLength = this.resultsGrid.length - 1;
    const resultsCount = longestOpPhaseIndex.temperatureResultsTable.rows[0].length - 1;
    for (let i = 0; i <= resGridLength; i++) {
      if (this.resultsGrid[i]['mdFt'] == longestOpPhaseIndex.temperatureResultsTable.rows[i][0]) {
        const currentUdt = longestOpPhaseIndex.temperatureResultsTable.rows[i][resultsCount];
        this.resultsGrid[i]['udt'] = this.multipleResultsFull[i][this.operationNames.length - 1] = currentUdt;
      } else {
        const prev = this.resultsGrid[i - 2];
        const next = this.resultsGrid[i - 1];
        const currentDepth = this.resultsGrid[i]['mdFt'];

        const interpolatedTemperature = prev['udt'] + (currentDepth - prev['mdFt']) * (next['udt'] - prev['udt']) / (next['mdFt'] - prev['mdFt']);

        const depthIndex = depths.findIndex(x => x >= currentDepth);
        this.resultsGrid[depthIndex]['udt'] = interpolatedTemperature;
        this.multipleResultsFull[depthIndex][this.operationNames.length - 1] = interpolatedTemperature;
      }
    }
    this.getColor(this.multipleResultsFull);
  }

  private remapResults(selectedPhase?: TemperatureResultPhase): void {
    if (!this.selectedOperation) {
      this.results = [];
      this.cols = [];
      this.resultsGrid = [];
      this.phases = null;
      this.missingOperations = true;
    }
    const selectedOperationResults = this.allOperationsResults.find(o => {
      const selectedOperation = this.selectedOperation;
      const selectedOperationId = selectedOperation.id;
      return o.operationId === selectedOperationId;
    });
    this.needToCalculate = !selectedOperationResults;
    if (this.needToCalculate && this.selectedSingleMultiple != 'singleOperation') {
      this.remapMultipleResults(0);
      return;
    }
    this.phases = selectedOperationResults ? selectedOperationResults.phases : null;
    if (!selectedPhase) {
      selectedPhase = this.phases ? this.phases[this.phases.length - 1] : null;
    }

    if (selectedPhase) {
      this.results = cloneDeep(selectedPhase.temperatureResultsTable.rows);
      if (isNaN(this.results[0][3])) {
        this.results = [];
        this.cols = [];
        this.resultsGrid = [];
        this.needToCalculate = true;
        this.calculationError = "PERICAL engine error while calculating operation.";
        return;
      }
      let headers = this.showFormationPoints ?
        selectedPhase.temperatureResultsTable.headers :
        selectedPhase.temperatureResultsTable.headers.filter(x => (!x.includes(`(${this.depthUnit})`) || x.includes('MD') || x.includes('TVD')));

      const headerCountOriginal = headers.length;
      const originalResults = cloneDeep(selectedPhase.temperatureResultsTable.rows);
      if (!this.showWellborePoints) {
        headers = headers.filter(x => (x.includes(`(${this.depthUnit})`) || !x.includes(`${this.diameterUnit}`)) && !x.includes('Inner Fluid'));
        const removeWellboreColumns = headerCountOriginal - headers.length;
        this.results.forEach(x => x.splice(2, removeWellboreColumns));
      }

      if (!this.showFormationPoints) {
        headers = headers.filter(x => !!x.includes(`(${this.depthUnit})`) || x.includes(`${this.diameterUnit}`) || x.includes('Inner Fluid'));
        const resLength = this.results[0].length;
        this.results.forEach(x => x.splice(headers.length, resLength));
      }

      this.cols = headers.map(h => {
        return {
          field: toCamelCaseString((h === `MD (${this.depthUnit})` || h === `TVD (${this.depthUnit})`) ? h.split(' ').shift() : h),
          header: h,
          valueFormatter: (params: any) => this.formatDecimal(params)
        };
      });

      this.cols.push({
        field: 'udt',
        header: `UDT`,
        valueFormatter: (params: any) => this.formatDecimal(params)
      })

      this.getColor(this.results);

      this.resultsGrid = this.results.map(r => zipObject(this.cols.map(c => c.field), r));

      const resGridLength = this.resultsGrid.length - 1;
      const resultsCount = this.results[0].length - 1;
      const originalResultsCount = originalResults[0].length - 1;
      for (let i = 0; i <= resGridLength; i++) {
        this.resultsGrid[i]['udt'] = originalResults[i][originalResultsCount];
        this.results[i][resultsCount + 1] = originalResults[i][originalResultsCount];
      }
    } else {
      this.needToCalculate = true;
      this.results = [];
      this.cols = [];
      this.resultsGrid = [];
      this.calculationError = !this.needToCalculate ? "PERICAL engine error while calculating operation." : "Please calculate PERICAL to see results.";
    }
  }

  private getOperationTypeName(): string {
    return this.operationTypes.find(ot => ot.value === this.selectedOperationType)?.label ?? '';
  }

  private getColor(res): void {
    const results = [...res];
    results.forEach((array, index) => {
      results[index] = array.slice(2);
    });

    let allResults = [].concat(...results).map(x => Math.round(x * 10) / 10);

    if (allResults.length > 0) {
      allResults = [...new Set(allResults.sort((a, b) => (a < b ? -1 : 1)))];
    }

    this.minTemp = allResults[0];
    this.maxTemp = allResults[allResults.length - 1];
  }

  public toggleHeatmap(event) {
    this.temperatureResultsStore.update(state => ({
      ...state,
      showHeatmap: event['srcElement']['checked']
    }));
  }

  private formatDecimal(value: any) {
    return value || value === 0
      ? (Math.trunc(value * 100) / 100).toLocaleString('en-US')
      : null;
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}
